import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { Company, CriteriumData } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-strategy-company-list',
  templateUrl: './strategy-company-list.component.html',
  // TODO: Split scss files by component
  styleUrls: ['../strategy-personalise/strategy-personalise.component.scss'],
  standalone: false
})
export class StrategyCompanyListComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Output() updateLoadingID = new EventEmitter<number>();
  @Output() showWhyInyova = new EventEmitter<boolean>();
  @Output() updateCompanyDetail = new EventEmitter<{ companyID: number; updated: boolean }>();
  @Input() appLocation: 'ch' | 'de';
  @Input() companyList: Company[] = [];
  @Input() wishedCompaniesIDs = [];
  @Input() blockedCompaniesIDs = [];
  @Input() listType = '';
  @Input() loadingID: number;
  @Input() enabledActions = true;
  @Input() selectedCompanyID = 0;
  @Input() stepStatus: number;
  @Input() view: string;
  @Input() activeTab: string;

  isMobile = false;
  MAX_ELEMENTS = 7;
  MIN_ELEMENTS = 3;
  maxElements = this.MAX_ELEMENTS;

  constructor(
    private store: Store,
    public trackingService: TrackingService,
    private translateService: TranslateService,
    private amplitudeService: AmplitudeService
  ) {
    this.isMobile = window.innerWidth <= MOBILE_VIEW_TRESHOLD;

    this.store.pipe(select(fromStrategy.selectActiveCompanyID), takeUntil(this.onDestroy$)).subscribe((selectedCompanyID) => {
      this.selectedCompanyID = selectedCompanyID;
    });

    this.store.pipe(select(fromAppRoot.selectCurrentAccountEditMode), takeUntil(this.onDestroy$)).subscribe((editMode) => {
      this.enabledActions = editMode;
    });

    this.elementsPerRow();
  }

  trackByCompanyID(index: number, company: Company): number {
    // this.isWishedCompany(company.id);
    return company.id;
  }

  showCompany($event: Event, company: Company): void {
    if (($event.target as HTMLElement).classList.contains('company-action')) return;

    if (this.selectedCompanyID === company.id) {
      this.selectedCompanyID = 0;
      return;
    }

    this.selectedCompanyID = company.id;

    this.updateCompanyDetail.emit({ companyID: company.id, updated: true });
    this.store.dispatch(StrategyActions.setCompanyDetailLoading());
    this.store.dispatch(StrategyActions.getCompanyDetail({ companyId: company.id }));
    this.trackingService.trackActivity(`[List] ${company.name}`);
  }

  openWhyInyova($event: Event): void {
    $event.stopPropagation();
    this.showWhyInyova.emit(true);
  }

  updateLoadingCompany(companyID: number): void {
    this.updateLoadingID.emit(companyID);
  }

  getWarningCopy(criteria: Partial<CriteriumData>[]): string {
    const dismatchedCriteriaTitles = criteria.map((a) => a.name).join(', ');
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${this.translateService.instant('new_strategy.matches_screen.criteria_dismatch')} ${dismatchedCriteriaTitles}`;
  }

  elementsPerRow() {
    this.maxElements = Math.min(this.MIN_ELEMENTS + Math.floor((window.innerWidth - 320) / 48), this.MAX_ELEMENTS);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'Strategy company list',
      consultation_method: 'hover'
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: Window }): void {
    this.elementsPerRow();
    if (event.target.innerWidth > MOBILE_VIEW_TRESHOLD) {
      this.isMobile = false;
    } else {
      this.isMobile = true;
    }
  }
}
